/* Copyright (c) 2025 Xperi. All Rights Reserved.  */

import { GroupingType } from 'client-code/episode-guide';

import { DeviceModel } from '../../core/device';
import { ContentType } from '../../core/enums';
import { NdvrInteractor } from '../../core/interactors';
import { Log } from '../../core/logging';
import { ScreenController } from '../../core/navigation/ScreenController';
import { SharedPrefConstants, SharedPreferences } from '../../core/storage';
import type { TGetState } from '../../core/tivo/tivo-redux-wrapper';
import { ListView } from '../../core/usecases';
import { ListTypes } from '../../core/utils';
import { AllEpisodesListUtility } from '../../views/ListViewScreen/AllEpisodesList/AllEpisodesListUtility';
import { getSeasonPickerOverlayList } from '../collectionscreen/collectionscreen.reducers';
import { setListEntryFocus, setListEntryPosition } from '../list/list.actions';
import { onKeyDownList, onKeyUpList, onMouseOutList, onMouseOverList } from '../list/list.reducers';
import { setListEntryPageActions } from '../listscreenview/listviewscreen.actions';
import { showGoToOverlay } from '../overlay/gotooverlay/gotooverlay.reducers';
import {
    GLOBAL_MOUSE_TRAP,
    showPCPinOverlayWithProgramRating,
} from '../overlay/pcpinoverlay/pcpinoverlay.reducers';
import { hideOverlayDialog } from '../overlaydialog/overlaydialog.reducers';
import { programScreenTransition } from '../screens/navigation/screensnavigation';
import {
    clearLastFocusedPickerState,
    onMouseOutSeasonPicker,
    onMouseOverSeasonPicker,
    removeSeasonPickerFromFocus,
    resetSeasonPickersFocus,
    setLastFocusedPickerState,
    setLastFocusedType,
    setSeasonPickerHighlight,
    setSeasonPickerInFocus,
} from '../seasonpicker/seasonpicker.reducers';
import { showWhisper } from '../whisper/whisper.reducer';

const doAdultParentalControlCheck =
    (isAdult: boolean, callBack: Function, ...callBackParams: any[]) =>
    (dispatch: Function) => {
        const services = new NdvrInteractor();
        if (isAdult && services.shouldHideMetadata()) {
            dispatch(
                showPCPinOverlayWithProgramRating(() => {
                    dispatch(callBack(...callBackParams));
                }, isAdult),
            );
            return;
        }
        dispatch(callBack(...callBackParams, showWhisper));
    };

const persistAllEpisodesListPosition = (
    listPosition: number,
    page: number,
    id: string,
    previousHighlight: number = AllEpisodesListUtility.MAX_ITEM_IN_PAGE_ALL_EPISODES - 1,
): void => {
    const sharedPreferences = DeviceModel.getSharedPreferences(
        SharedPrefConstants.LIST_SCREEN_PREF_KEY,
    );
    const listViewState = sharedPreferences.getJson(SharedPrefConstants.LISTVIEW_STATE) || {};
    const allEpisodesState = {
        lastHighlight: listPosition,
        lastPage: page,
        previousHighlight,
    };

    if (id) {
        listViewState[id] = allEpisodesState;
        const listSharedPreferenceEditor = sharedPreferences.edit();
        listSharedPreferenceEditor.putJson(SharedPrefConstants.LISTVIEW_STATE, listViewState);
        listSharedPreferenceEditor.apply();
    }
};

const setHighlightIndexFromPreviousPage = (id: string, newPage: number) => {
    const sharedPreferences = DeviceModel.getSharedPreferences(
        SharedPrefConstants.LIST_SCREEN_PREF_KEY,
    );
    const listViewState = sharedPreferences.getJson(SharedPrefConstants.LISTVIEW_STATE) || {};

    const previousPageHighlightIndex = listViewState[id].previousHighlight;
    if (id && listViewState[id]) {
        listViewState[id].lastHighlight = previousPageHighlightIndex;
        listViewState[id].lastPage = newPage;
    }

    const listSharedPreferenceEditor = sharedPreferences.edit();
    listSharedPreferenceEditor.putJson(SharedPrefConstants.LISTVIEW_STATE, listViewState);
    listSharedPreferenceEditor.apply();
};

const initializeHighlightIndices = (id: string, newPage: number) => {
    const sharedPreferences = DeviceModel.getSharedPreferences(
        SharedPrefConstants.LIST_SCREEN_PREF_KEY,
    );
    const listViewState = sharedPreferences.getJson(SharedPrefConstants.LISTVIEW_STATE) || {};
    if (id && listViewState[id]) {
        listViewState[id].lastHighlight = 0;
        listViewState[id].lastPage = newPage;
        listViewState[id].previousHighlight =
            AllEpisodesListUtility.MAX_ITEM_IN_PAGE_ALL_EPISODES - 1;
    }

    const listSharedPreferenceEditor = sharedPreferences.edit();
    listSharedPreferenceEditor.putJson(SharedPrefConstants.LISTVIEW_STATE, listViewState);
    listSharedPreferenceEditor.apply();
};

export const onKeyUpAllepisodesList =
    (flattenedList: any[], page: number, id: string) =>
    (dispatch: Function, getState: TGetState): void => {
        if (!flattenedList || !Array.isArray(flattenedList) || flattenedList.length === 0) return;
        const listType = ListTypes.ALL_EPISODES_LIST;
        const listPosition = getState().list[listType]?.position ?? 0;
        const previousPosition = listPosition - 1;

        const prevPageRequired = previousPosition < 0;
        const prevPageAvailable = page > 0;

        if (prevPageRequired && !prevPageAvailable) return;

        if (prevPageRequired && prevPageAvailable) {
            persistAllEpisodesListPosition(listPosition, page, id);
            dispatch(setListEntryPageActions(page - 1));
            setHighlightIndexFromPreviousPage(id, page - 1);
            return;
        }

        const currentItem = flattenedList[listPosition];
        const previousItem = flattenedList[previousPosition];
        const isPreviousItemHeader = previousItem?.isHeader;
        if (isPreviousItemHeader) {
            if (previousItem.pickerType === 'picker') {
                dispatch(onKeyUpList(ListTypes.ALL_EPISODES_LIST));
                dispatch(setListEntryFocus(ListTypes.ALL_EPISODES_LIST, false));
                dispatch(setSeasonPickerInFocus(previousItem.seasonTitle));
            }

            if (previousItem.pickerType === 'header') {
                const newPreviousPosition = previousPosition - 1;
                const isNewPreviousPageRequired = newPreviousPosition < 0;
                if (isNewPreviousPageRequired && !prevPageAvailable) {
                    return;
                }
                if (isNewPreviousPageRequired) {
                    persistAllEpisodesListPosition(listPosition, page, id);
                    dispatch(setListEntryPageActions(page - 1));
                    setHighlightIndexFromPreviousPage(id, page - 1);
                    return;
                }
                dispatch(setListEntryPosition(listType, previousPosition - 1 || 0));
            }
        } else {
            if (currentItem?.isHeader && currentItem?.pickerType === 'picker') {
                dispatch(removeSeasonPickerFromFocus(currentItem.seasonTitle));
            }
            dispatch(onKeyUpList(ListTypes.ALL_EPISODES_LIST));
        }
    };

export const onKeyDownAllepisodesList =
    (flattenedList: any[], page: number, totalCount: number, id: string) =>
    (dispatch: Function, getState: TGetState): void => {
        if (!flattenedList || !Array.isArray(flattenedList) || flattenedList.length === 0) return;
        const listType = ListTypes.ALL_EPISODES_LIST;
        const listPosition = getState().list[listType]?.position ?? 0;
        const nextPosition = listPosition + 1;

        const newPageRequired = nextPosition >= flattenedList.length;
        const newPageAvailable =
            page * AllEpisodesListUtility.MAX_ITEM_IN_PAGE_ALL_EPISODES + nextPosition < totalCount;

        if (newPageRequired && !newPageAvailable) return;

        if (newPageRequired && newPageAvailable) {
            persistAllEpisodesListPosition(listPosition, page, id);
            dispatch(setListEntryPageActions(page + 1));
            initializeHighlightIndices(id, page + 1);
            return;
        }

        const currentItem = flattenedList[listPosition];
        const nextItem = flattenedList[nextPosition];
        const isNextItemHeader = nextItem?.isHeader;
        if (isNextItemHeader) {
            if (nextItem.pickerType === 'picker') {
                dispatch(onKeyDownList(ListTypes.ALL_EPISODES_LIST));
                dispatch(setListEntryFocus(ListTypes.ALL_EPISODES_LIST, false));
                dispatch(setSeasonPickerInFocus(nextItem.seasonTitle));
            }
            if (nextItem.pickerType === 'header') {
                const newNextPosition = nextPosition + 1;
                const isNewPageRequired = newNextPosition >= flattenedList.length;
                if (isNewPageRequired && !newPageAvailable) {
                    return;
                }
                if (isNewPageRequired) {
                    persistAllEpisodesListPosition(listPosition, page, id);
                    dispatch(setListEntryPageActions(page + 1));
                    initializeHighlightIndices(id, page + 1);
                    return;
                }
                dispatch(setListEntryPosition(listType, nextPosition + 1 || 0));
            }
        } else {
            if (currentItem?.isHeader && currentItem?.pickerType === 'picker') {
                dispatch(removeSeasonPickerFromFocus(currentItem.seasonTitle));
            }
            dispatch(onKeyDownList(listType));
        }
    };

export const onKeyRightAllepisodesList =
    (flattenedList: any[]) =>
    (dispatch: Function, getState: TGetState): void => {
        if (!flattenedList || !Array.isArray(flattenedList)) return;
        const listType = ListTypes.ALL_EPISODES_LIST;
        const listPosition = getState().list[listType]?.position ?? 0;
        const currentItem = flattenedList[listPosition];
        if (currentItem?.isHeader && currentItem.pickerType === 'picker') {
            const nextPosition =
                (getState().seasonPicker.pickers[currentItem.seasonTitle].index || 0) + 1;
            const totalItems = currentItem.pickerItems.length;
            if (nextPosition >= totalItems) {
                return;
            }
            dispatch(setSeasonPickerHighlight(currentItem.seasonTitle, nextPosition));
        }
    };

export const onKeyLeftAllepisodesList =
    (flattenedList: any[]) =>
    (dispatch: Function, getState: TGetState): void => {
        if (!flattenedList || !Array.isArray(flattenedList)) return;
        const listType = ListTypes.ALL_EPISODES_LIST;
        const listPosition = getState().list[listType]?.position ?? 0;
        const currentItem = flattenedList[listPosition];
        if (currentItem?.isHeader && currentItem.pickerType === 'picker') {
            const previousPosition =
                (getState().seasonPicker.pickers[currentItem.seasonTitle].index || 0) - 1;
            if (previousPosition < 0) {
                return;
            }
            dispatch(setSeasonPickerHighlight(currentItem.seasonTitle, previousPosition));
        }
    };

export const onKeyOKAllepisodesList =
    (flattenedList: any[], id: string) =>
    (dispatch: Function, getState: TGetState): void => {
        if (!flattenedList || !Array.isArray(flattenedList)) return;
        const listType = ListTypes.ALL_EPISODES_LIST;
        const listPosition = getState().list[listType]?.position ?? 0;
        const currentItem = flattenedList[listPosition];

        if (currentItem?.isHeader && currentItem?.pickerType === 'picker') {
            const selectedSeason =
                currentItem?.pickerItems?.[
                    getState().seasonPicker?.pickers[currentItem.seasonTitle]?.index || 0
                ]?.text || '';
            dispatch(onKeyOkOnAllEpisodesListSeasonPickerHandler(selectedSeason));
        } else {
            if (!currentItem || currentItem?.isHeader) return;
            const { page } = getState().listViewScreen;
            persistAllEpisodesListPosition(listPosition, page, id);
            const currScreen = ScreenController.getInstance().getScreen();
            const currScreenProperties = currScreen.getProperties() || {};
            currScreen.setProperties({
                ...currScreenProperties,
                highlight: listPosition,
            });
            dispatch(
                doAdultParentalControlCheck(currentItem?.isAdult, programScreenTransition, {
                    id: currentItem.contentId,
                    contentType: ContentType.CONTENT_ID,
                }),
            );
        }
    };

export const onKeyBackAllepisodesList =
    (flattenedList: any[], id: string) =>
    (dispatch: Function, getState: TGetState): void => {
        if (!flattenedList || !Array.isArray(flattenedList)) return;
        const listType = ListTypes.ALL_EPISODES_LIST;
        const listPosition = getState().list[listType]?.position ?? 0;
        const currentItem = flattenedList[listPosition];

        if (!currentItem) return;

        // Store position only for non-headers
        if (!currentItem?.isHeader) {
            const { page } = getState().listViewScreen;
            persistAllEpisodesListPosition(listPosition, page, id);
        }
    };

export const onKeyOkOnAllEpisodesListSeasonPickerHandler =
    (seasonOrYear?: string) => async (dispatch: Function) => {
        const listItemInfo = new SharedPreferences(
            SharedPrefConstants.LIST_SCREEN_PREF_KEY,
        ).getJson(SharedPrefConstants.LIST_VIEW_SCREEN_SELECTED_OPTION) as any;

        const id = listItemInfo?.id || '';
        const computedSeasonData = new ListView().getComputedSeasonData(id);

        if (computedSeasonData) {
            dispatch(resetSeasonPickersFocus());
            // Check if it's a season group (range like "1-5" or "1-'25")
            if (seasonOrYear && /^\d+[-']/.test(seasonOrYear)) {
                // It's a range, extract the seasons for this range
                const parts = seasonOrYear.split('-');
                const startSeason = Number(parts[0]);
                const endSeasonStr = parts[1].replace(/'/g, '');
                const endSeason = Number(endSeasonStr);
                const seasonsOrYears = getSeasonPickerOverlayList(
                    computedSeasonData.seasonData,
                    startSeason,
                    endSeason,
                );
                dispatch(
                    showGoToOverlay(
                        computedSeasonData.seasonData.grouping === GroupingType.Season,
                        seasonsOrYears,
                    ),
                );
            } else {
                // Single season/year selected - jump to that season
                const selectedSeasonNumber = seasonOrYear === '+' ? -1 : Number(seasonOrYear);
                const { totalEpisodeTillSeason } = computedSeasonData;

                // Find index of selected season in the seasons array
                const selectedSeasonIndex =
                    computedSeasonData.seasonData.seasons?.findIndex(
                        (season) => season.seasonOrYear === selectedSeasonNumber,
                    ) ?? -1;

                if (selectedSeasonIndex >= 0) {
                    // Calculate offset: total episodes BEFORE this season
                    const episodeBefore =
                        selectedSeasonIndex > 0
                            ? totalEpisodeTillSeason[selectedSeasonIndex - 1]
                            : 0;

                    // Number of headers (season boundaries) before this season
                    const headersBefore =
                        selectedSeasonIndex *
                        AllEpisodesListUtility.SEASON_HEADER_SIZE_ALL_EPISODES;

                    // Total items (episodes + headers) before this season
                    const offsetWithHeaders = episodeBefore + headersBefore;

                    // Round to nearest page boundary
                    const pageSize = AllEpisodesListUtility.MAX_ITEM_IN_PAGE_ALL_EPISODES;
                    const page = Math.floor(offsetWithHeaders / pageSize);
                    const alignedOffset = page * pageSize;
                    const positionInPage = offsetWithHeaders - alignedOffset;
                    persistAllEpisodesListPosition(positionInPage + 1, page, id);
                    dispatch(setListEntryPageActions(page));
                    dispatch(setListEntryPosition(ListTypes.ALL_EPISODES_LIST, positionInPage + 1));
                } else {
                    Log.e('AllEpisodesList', `Season ${seasonOrYear} not found`);
                }
            }
        } else {
            Log.e('AllEpisodesList', 'Computed season data not available');
        }
    };

export const onKeyOkOnAllEpisodesListGoToOverlay =
    () => async (dispatch: Function, getState: TGetState) => {
        const { overlayDialog, overlayHighlight } = getState();
        const { verticalPosition } = overlayHighlight;
        const { uiProperties } = overlayDialog;
        const { options } = uiProperties;
        const selectedOption = options[verticalPosition];

        dispatch(hideOverlayDialog());
        await dispatch(onKeyOkOnAllEpisodesListSeasonPickerHandler(selectedOption?.value || ''));
    };

export const onKeyBackOnAllEpisodesListGoToOverlay =
    () => (dispatch: Function, getState: TGetState) => {
        dispatch(hideOverlayDialog());

        const listItemInfo = new SharedPreferences(
            SharedPrefConstants.LIST_SCREEN_PREF_KEY,
        ).getJson(SharedPrefConstants.LIST_VIEW_SCREEN_SELECTED_OPTION) as any;

        if (
            listItemInfo?.type === 'allEpisodes' &&
            listItemInfo?.flattenedList &&
            Array.isArray(listItemInfo.flattenedList)
        ) {
            const listType = ListTypes.ALL_EPISODES_LIST;
            const listPosition = getState().list[listType]?.position ?? 0;
            if (listPosition < 0 || listPosition >= listItemInfo.flattenedList.length) return;
            const currentItem = listItemInfo.flattenedList[listPosition];

            if (currentItem?.isHeader && currentItem?.pickerType === 'picker') {
                dispatch(setSeasonPickerInFocus(currentItem.seasonTitle));
            }
        }
    };

export const onMouseClickOnAllEpisodesListGoToOverlay =
    () => (dispatch: Function, getState: Function) => {
        const { value } = getState().inputFactory;

        if (value === GLOBAL_MOUSE_TRAP) {
            dispatch(hideOverlayDialog());
        } else dispatch(onKeyOkOnAllEpisodesListGoToOverlay());
    };

export const onMouseOverAllEpisodesList =
    (value: string, flattenedList?: any[]) => (dispatch: Function, getState: Function) => {
        if (value && typeof value === 'string' && value.includes('season-picker')) {
            const parts = value.split('_');
            const pickerId = parts[0];
            const itemIndex = parseInt(parts[1], 10);

            // Find the header position for this picker in the flattened list
            const headerPosition =
                flattenedList && flattenedList.length > 0
                    ? flattenedList.findIndex(
                          (item) =>
                              item.isHeader &&
                              item.pickerType === 'picker' &&
                              item.seasonTitle === pickerId,
                      )
                    : -1;

            dispatch(onMouseOverSeasonPicker(pickerId, itemIndex));
            if (headerPosition !== -1) {
                dispatch(setLastFocusedPickerState(pickerId, itemIndex, headerPosition));
            }
            return;
        }
        // Reset picker focus when mousing over an episode (non-picker item)
        dispatch(resetSeasonPickersFocus());
        dispatch(onMouseOverList());
    };

export const onMouseOutAllEpisodesList = (value: string) => (dispatch: Function) => {
    if (value && typeof value === 'string' && value.includes('season-picker')) {
        const parts = value.split('_');
        const pickerId = parts[0];
        dispatch(onMouseOutSeasonPicker(pickerId));
        return;
    }

    dispatch(onMouseOutList());
};

export const onMouseClickAllEpisodesList = (value: string) => (dispatch: Function) => {
    if (value && typeof value === 'string' && value.includes('season-picker')) {
        const dataString = value.replace('_season-picker', '');
        const parts = dataString.split('_');
        const seasonText = parts[parts.length - 1];
        if (seasonText) {
            dispatch(onKeyOkOnAllEpisodesListSeasonPickerHandler(seasonText));
        }
    }
};

/**
 * Handle ghost mode for allEpisodes when last focused was a picker.
 * Focuses the picker and disables ghost mode.
 * Uses stored lastFocusedPickerState to directly restore the picker header position and index.
 */
export const handleAllEpisodesGhostModeForPicker =
    () => (dispatch: Function, getState: Function) => {
        const { lastFocusedType, lastFocusedPickerState } = getState().seasonPicker;

        const onDemandSharedPref = new SharedPreferences(
            SharedPrefConstants.LIST_SCREEN_PREF_KEY,
        ).getJson(SharedPrefConstants.LIST_VIEW_SCREEN_SELECTED_OPTION) as any;
        if (
            onDemandSharedPref?.type === 'allEpisodes' &&
            onDemandSharedPref?.flattenedList &&
            lastFocusedType === 'picker' &&
            lastFocusedPickerState !== null
        ) {
            const { seasonTitle, pickerIndex, headerPosition } = lastFocusedPickerState;
            dispatch(setListEntryPosition(ListTypes.ALL_EPISODES_LIST, headerPosition));
            dispatch(setSeasonPickerHighlight(seasonTitle, pickerIndex));
            dispatch(setSeasonPickerInFocus(seasonTitle));
            dispatch(clearLastFocusedPickerState());
            dispatch(setLastFocusedType(''));
        }
    };
